import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { storeSettingsService } from '../../services/storeSettings.service';
import { getBusiness } from '../../services/auth.service';
import { AWS_URL } from '../../services/http.service';
import { toast } from 'react-toastify';
import { useSelector } from "react-redux";
import { initTracking, syncTracking, trackIntent, trackPageView } from '../../components/common/utlity/tracking';
import { LogoLoading } from '../../components/common/utlity/utility';

// Layout Selector Modal Component
function LayoutSelectorModal({ open, onClose, onSelectLayout, currentLayout, theme }) {
    const { businessUuid } = useParams();

  useEffect(() => {
    if (!businessUuid) return;

    initTracking(businessUuid);
    trackPageView(window.location.pathname);

    const interval = setInterval(() => {
      syncTracking("interval");
    }, 10 * 60 * 1000);

    return () => clearInterval(interval);
  }, [businessUuid]);
  const layouts = [
    {
      id: 'fullscreen-banner',
      name: 'Fullscreen Banner',
      description: 'Cover the entire screen with banner image/color',
      icon: '🖼️',
      category: 'Banner Layouts',
      previewColor: '#4f46e5'
    },
    {
      id: 'split-screen',
      name: 'Split Screen',
      description: 'Half banner, half welcome message',
      icon: '📱',
      category: 'Banner Layouts',
      previewColor: '#7c3aed'
    },
    {
      id: 'minimal',
      name: 'Minimal Header',
      description: 'Small banner with quick navigation',
      icon: '📄',
      category: 'Simple Layouts',
      previewColor: '#0ea5e9'
    },
    {
      id: 'carousel',
      name: 'Banner Carousel',
      description: 'Multiple banners in a slideshow',
      icon: '🔄',
      category: 'Dynamic Layouts',
      previewColor: '#10b981'
    },
    {
      id: 'gradient',
      name: 'Gradient Overlay',
      description: 'Banner with gradient text overlay',
      icon: '🌈',
      category: 'Creative Layouts',
      previewColor: '#f59e0b'
    },
    {
      id: 'centered',
      name: 'Centered Elegant',
      description: 'Elegant centered content with background',
      icon: '✨',
      category: 'Modern Layouts',
      previewColor: '#8b5cf6'
    },
    {
      id: 'glass-morphism',
      name: 'Glass Morphism',
      description: 'Modern glass effect design',
      icon: '🥃',
      category: 'Modern Layouts',
      previewColor: '#06b6d4'
    },
    {
      id: 'parallax',
      name: 'Parallax Effect',
      description: 'Depth effect with layered elements',
      icon: '🌌',
      category: 'Dynamic Layouts',
      previewColor: '#ec4899'
    },
    {
      id: 'product-showcase',
      name: 'Product Showcase',
      description: 'Showcase featured products upfront',
      icon: '📦',
      category: 'Product Layouts',
      previewColor: '#f97316'
    },
    {
      id: 'dark-minimal',
      name: 'Dark Minimal',
      description: 'Dark theme with minimal design',
      icon: '🌙',
      category: 'Simple Layouts',
      previewColor: '#18181b'
    },
    {
      id: 'animated',
      name: 'Animated Background',
      description: 'Animated background with particles',
      icon: '✨',
      category: 'Dynamic Layouts',
      previewColor: '#3b82f6'
    },
    {
      id: 'sidebar',
      name: 'Sidebar Navigation',
      description: 'Sidebar with main content',
      icon: '📋',
      category: 'Navigation Layouts',
      previewColor: '#0d9488'
    },
    {
      id: 'card-grid',
      name: 'Card Grid',
      description: 'Grid of feature cards',
      icon: '🔲',
      category: 'Modern Layouts',
      previewColor: '#6366f1'
    },
    {
      id: 'masonry',
      name: 'Masonry Grid',
      description: 'Pinterest-style image grid',
      icon: '🧱',
      category: 'Creative Layouts',
      previewColor: '#84cc16'
    },
    {
      id: 'hero-sidebar',
      name: 'Hero with Sidebar',
      description: 'Hero section with sidebar info',
      icon: '🎯',
      category: 'Banner Layouts',
      previewColor: '#8b5cf6'
    },
    {
      id: 'split-content',
      name: 'Split Content',
      description: 'Multiple content sections split',
      icon: '📊',
      category: 'Content Layouts',
      previewColor: '#14b8a6'
    },
    {
      id: 'floating-elements',
      name: 'Floating Elements',
      description: 'Floating design elements',
      icon: '🎈',
      category: 'Creative Layouts',
      previewColor: '#f43f5e'
    },
    {
      id: 'timeline',
      name: 'Timeline Story',
      description: 'Tell your brand story',
      icon: '📜',
      category: 'Story Layouts',
      previewColor: '#f59e0b'
    },
    {
      id: 'testimonial',
      name: 'Testimonial Focus',
      description: 'Customer testimonials focus',
      icon: '💬',
      category: 'Social Proof Layouts',
      previewColor: '#10b981'
    }
  ];

  const categories = [...new Set(layouts.map(l => l.category))];

  if (!open) return null;

  return (
    <div className='fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[9999] p-4 backdrop-blur-sm'>
      <div 
        className='bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col'
        style={{ fontFamily: theme?.fontFamily }}
      >
        {/* Header */}
        <div className='p-6 border-b bg-gradient-to-r from-indigo-50 to-purple-50'>
          <div className='flex items-center justify-between'>
            <div>
              <h2 className='text-2xl font-bold text-gray-900'>Choose Landing Page Layout</h2>
              <p className='text-gray-600'>Select how your store appears to visitors</p>
            </div>
            <button
              onClick={onClose}
              className='p-2 hover:bg-gray-100 rounded-full transition-colors'
            >
              <span className='text-2xl text-gray-500 hover:text-gray-700'>&times;</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className='flex-1 overflow-y-auto p-6'>
          {categories.map(category => (
            <div key={category} className='mb-8'>
              <h3 className='text-lg font-semibold text-gray-800 mb-4 border-b pb-2'>
                {category}
              </h3>
              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
                {layouts
                  .filter(layout => layout.category === category)
                  .map(layout => (
                    <div
                      key={layout.id}
                      onClick={() => onSelectLayout(layout.id)}
                      className={`border-2 rounded-xl p-4 cursor-pointer transition-all hover:shadow-lg hover:border-indigo-300 group ${
                        currentLayout === layout.id 
                          ? 'ring-2 ring-indigo-500 border-indigo-500 bg-indigo-50' 
                          : 'border-gray-200'
                      }`}
                    >
                      <div className='flex items-start justify-between mb-3'>
                        <div 
                          className='text-3xl p-3 rounded-lg'
                          style={{ backgroundColor: `${layout.previewColor}20` }}
                        >
                          {layout.icon}
                        </div>
                      </div>
                      <h4 className='font-bold text-gray-900 mb-1'>{layout.name}</h4>
                      <p className='text-sm text-gray-600 mb-3'>{layout.description}</p>
                      {currentLayout === layout.id && (
                        <div className='mt-2'>
                          <span className='inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800'>
                            ✓ Currently Active
                          </span>
                        </div>
                      )}
                      <div className='mt-4'>
                        <button 
                          className={`w-full py-2 text-sm font-medium rounded-lg transition-opacity ${
                            currentLayout === layout.id
                              ? 'bg-indigo-600 text-white'
                              : 'bg-indigo-50 text-indigo-600 opacity-0 group-hover:opacity-100'
                          }`}
                        >
                          {currentLayout === layout.id ? 'Active' : 'Select Layout'}
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className='p-6 border-t bg-gray-50 flex justify-between items-center'>
          <div className='text-sm text-gray-600'>
            Changes will be visible immediately to all visitors
          </div>
          <div className='flex gap-3'>
            <button
              onClick={onClose}
              className='px-6 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-100'
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Button Component
function ThemeButton({ children, onClick, variant = 'primary', theme, className = '' }) {
  const baseStyle = {
    fontFamily: theme?.fontFamily || 'Inter',
    borderRadius: theme?.buttonStyle === 'pill' ? '9999px' : 
                 theme?.buttonStyle === 'rounded' ? '12px' : '4px',
    padding: '12px 32px',
    fontSize: '16px',
    fontWeight: '600',
    transition: 'all 0.3s ease',
    border: 'none',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  };

  const variants = {
    primary: {
      backgroundColor: theme?.buttonColor || '#4f46e5',
      color: '#ffffff',
      boxShadow: `0 4px 20px ${theme?.buttonColor || '#4f46e5'}40`,
    },
    secondary: {
      backgroundColor: 'transparent',
      color: '#ffffff',
      border: `2px solid #ffffff`,
    },
    outline: {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      color: '#ffffff',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
    },
    dark: {
      backgroundColor: '#1f2937',
      color: '#ffffff',
      border: 'none',
    }
  };

  const style = {
    ...baseStyle,
    ...variants[variant],
  };

  return (
    <button
      onClick={onClick}
      style={style}
      className={`hover:scale-105 active:scale-95 ${className}`}
    >
      {children}
    </button>
  );
}

// Floating Particles Component for animated background
function FloatingParticles() {
  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none">
      {[...Array(20)].map((_, i) => (
        <div
          key={i}
          className="absolute rounded-full bg-white/10 animate-float"
          style={{
            width: `${Math.random() * 60 + 20}px`,
            height: `${Math.random() * 60 + 20}px`,
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 5}s`,
            animationDuration: `${Math.random() * 10 + 10}s`,
          }}
        />
      ))}
    </div>
  );
}

// Enhanced Animated Layout Component
function EnhancedAnimatedLayout({ storeInfo, storeTheme, handleEnterStore, setShowNavigation }) {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [activeEffect, setActiveEffect] = useState('particles');

  const effects = [
    { id: 'particles', name: 'Particles', icon: '✨' },
    { id: 'waves', name: 'Waves', icon: '🌊' },
    { id: 'grid', name: 'Grid', icon: '🔳' },
    { id: 'stars', name: 'Stars', icon: '⭐' },
  ];

  // Handle mouse movement for interactive effects
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const InteractiveParticles = () => (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(100)].map((_, i) => (
        <div
          key={i}
          className="absolute rounded-full bg-gradient-to-r from-cyan-400/20 to-blue-400/20 animate-float"
          style={{
            width: `${Math.random() * 40 + 10}px`,
            height: `${Math.random() * 40 + 10}px`,
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 5}s`,
            animationDuration: `${Math.random() * 10 + 15}s`,
            transform: `translate(${(mousePosition.x / window.innerWidth - 0.5) * 20}px, 
                        ${(mousePosition.y / window.innerHeight - 0.5) * 20}px)`,
          }}
        />
      ))}
    </div>
  );

  const WaveEffect = () => (
    <div className="absolute inset-0 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/30 via-purple-900/30 to-pink-900/30" />
      {[...Array(3)].map((_, i) => (
        <div
          key={i}
          className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-cyan-500/20 to-transparent"
          style={{
            animation: `wave ${15 + i * 5}s ease-in-out infinite`,
            animationDelay: `${i * 2}s`,
            clipPath: `polygon(0% ${100 - i * 10}%, 100% ${90 - i * 5}%, 100% 100%, 0% 100%)`,
          }}
        />
      ))}
    </div>
  );

  const GridEffect = () => (
    <div className="absolute inset-0" style={{
      backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                      linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
      backgroundSize: '50px 50px',
      animation: 'gridMove 20s linear infinite',
    }}>
      {[...Array(20)].map((_, i) => (
        <div
          key={i}
          className="absolute w-2 h-2 bg-cyan-400/50 rounded-full animate-ping"
          style={{
            left: `${(i * 5) % 100}%`,
            top: `${(i * 7) % 100}%`,
            animationDelay: `${i * 0.5}s`,
          }}
        />
      ))}
    </div>
  );

  const StarsEffect = () => (
    <div className="absolute inset-0">
      {[...Array(50)].map((_, i) => {
        const size = Math.random() * 3 + 1;
        return (
          <div
            key={i}
            className="absolute bg-white rounded-full animate-twinkle"
            style={{
              width: `${size}px`,
              height: `${size}px`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${Math.random() * 3 + 2}s`,
              boxShadow: `0 0 ${size * 2}px ${size}px rgba(255,255,255,0.3)`,
            }}
          />
        );
      })}
      {[...Array(5)].map((_, i) => (
        <div
          key={`shooting-${i}`}
          className="absolute w-1 h-1 bg-white rounded-full animate-shooting-star"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${i * 3}s`,
          }}
        />
      ))}
    </div>
  );

  const renderEffect = () => {
    switch (activeEffect) {
      case 'waves': return <WaveEffect />;
      case 'grid': return <GridEffect />;
      case 'stars': return <StarsEffect />;
      default: return <InteractiveParticles />;
    }
  };

  return (
    <div className='relative min-h-screen overflow-hidden bg-gradient-to-br from-blue-900 via-purple-900 to-pink-900'>
      {/* Background Effects */}
      {renderEffect()}
      
      {/* Animated Orbs */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-full blur-3xl animate-pulse-slow" />
        <div className="absolute inset-10 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full blur-2xl animate-pulse animation-delay-1000" />
      </div>
      
      <div className="absolute bottom-1/4 right-1/4 w-64 h-64">
        <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-full blur-3xl animate-pulse-slow animation-delay-1500" />
      </div>
      
      {/* Effect Selector */}
      <div className="absolute top-4 right-4 z-20 backdrop-blur-sm bg-black/30 rounded-2xl p-4">
        <div className="flex gap-2 mb-2">
          {effects.map(effect => (
            <button
              key={effect.id}
              onClick={() => setActiveEffect(effect.id)}
              className={`p-2 rounded-lg transition-all ${activeEffect === effect.id 
                ? 'bg-white/20 text-white' 
                : 'hover:bg-white/10 text-white/60'}`}
              title={effect.name}
            >
              <span className="text-xl">{effect.icon}</span>
            </button>
          ))}
        </div>
        <div className="text-white text-xs text-center">
          Click effects to change
        </div>
      </div>
      
      <div className='relative z-10 min-h-screen flex items-center justify-center p-8'>
        <div className='text-center max-w-4xl'>
          {/* Main Logo Animation */}
          <div className='mb-8 relative'>
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full blur-xl opacity-70 animate-pulse" />
            <div className="relative inline-block p-8 rounded-full bg-gradient-to-r from-cyan-500/20 to-blue-500/20 backdrop-blur-sm border border-white/20">
              <div className="relative w-32 h-32 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center animate-spin-slow">
                <div className="absolute inset-4 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 animate-spin-reverse">
                  <div className="absolute inset-4 rounded-full bg-black flex items-center justify-center">
                    <span className="text-4xl animate-bounce">⚡</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Animated Title */}
          <h1 
            className='text-5xl md:text-7xl font-bold mb-6 relative'
            style={{ fontFamily: storeTheme?.fontFamily }}
          >
            <span className="relative inline-block">
              <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent animate-gradient-text">
                {storeInfo?.businessName}
              </span>
              <span className="absolute -inset-1 bg-gradient-to-r from-cyan-400/20 via-blue-400/20 to-purple-400/20 blur-xl -z-10 animate-pulse" />
            </span>
          </h1>
          
          {/* Animated Subtitle */}
          <p className='text-xl text-gray-300 mb-12 max-w-2xl mx-auto relative'>
            <span className="backdrop-blur-sm bg-black/30 p-4 rounded-2xl border border-white/10 inline-block animate-fade-in-up">
              {storeInfo?.businessDescription || 'Experience the future of shopping with dynamic animations'}
            </span>
          </p>
          
          {/* Interactive Buttons */}
          <div className='space-y-6 animate-fade-in-up animation-delay-400'>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <ThemeButton
                onClick={handleEnterStore}
                variant='primary'
                theme={storeTheme}
                className="relative overflow-hidden group"
              >
                <span className="relative z-10 flex items-center gap-2">
                  <span className="animate-pulse">✨</span>
                  Enter Experience
                  <span className="animate-pulse">✨</span>
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute inset-0 animate-shimmer" />
              </ThemeButton>
              
              <ThemeButton
                onClick={() => setActiveEffect(effects[(effects.findIndex(e => e.id === activeEffect) + 1) % effects.length].id)}
                variant='outline'
                theme={storeTheme}
                className="backdrop-blur-sm"
              >
                <span className="flex items-center gap-2">
                  <span className="animate-spin-slow">🌀</span>
                  Change Effect
                </span>
              </ThemeButton>
            </div>
            
            {/* Interactive Features */}
            <div className='flex flex-wrap justify-center gap-8 mt-8'>
              {[
                { icon: '🎯', text: 'Interactive', color: 'from-cyan-400 to-blue-400' },
                { icon: '🚀', text: 'Dynamic', color: 'from-purple-400 to-pink-400' },
                { icon: '💫', text: 'Modern', color: 'from-yellow-400 to-orange-400' },
                { icon: '🌈', text: 'Colorful', color: 'from-green-400 to-emerald-400' },
              ].map((feature, idx) => (
                <div 
                  key={idx}
                  className="text-center group cursor-pointer"
                >
                  <div className={`relative w-20 h-20 rounded-2xl bg-gradient-to-br ${feature.color} 
                    flex items-center justify-center text-3xl mb-2
                    transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-300
                    shadow-lg group-hover:shadow-2xl`}>
                    <div className="absolute inset-0 rounded-2xl bg-white/10 backdrop-blur-sm" />
                    <span className="relative z-10 animate-bounce" style={{ animationDelay: `${idx * 200}ms` }}>
                      {feature.icon}
                    </span>
                  </div>
                  <p className='text-white text-sm font-medium mt-2'>{feature.text}</p>
                </div>
              ))}
            </div>
          </div>
          
          {/* Floating Elements */}
          <div className="mt-16">
            <div className="flex justify-center gap-4">
              <div className="w-4 h-4 bg-cyan-400 rounded-full animate-bounce" />
              <div className="w-4 h-4 bg-blue-400 rounded-full animate-bounce animation-delay-200" />
              <div className="w-4 h-4 bg-purple-400 rounded-full animate-bounce animation-delay-400" />
              <div className="w-4 h-4 bg-pink-400 rounded-full animate-bounce animation-delay-600" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Layout Components
function CenteredElegantLayout({ storeInfo, storeTheme, storeBanner, getBannerUrl, handleEnterStore, setShowNavigation }) {
  const bannerUrl = getBannerUrl();
  const bannerColor = storeBanner?.type === 'color' ? storeBanner.color : storeTheme?.primaryColor || '#4f46e5';

  return (
    <div className='relative min-h-screen flex items-center justify-center overflow-hidden'>
      {bannerUrl ? (
        <img
          src={bannerUrl}
          alt='Store Banner'
          className='absolute inset-0 w-full h-full object-cover'
        />
      ) : (
        <div 
          className='absolute inset-0 w-full h-full'
          style={{ backgroundColor: bannerColor }}
        />
      )}
      
      <div className='absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-transparent' />
      
      <div className='relative z-10 text-center px-4 max-w-3xl mx-auto'>
        <div className='mb-8'>
          <div className='inline-block p-1 rounded-full bg-gradient-to-r from-white/20 to-transparent backdrop-blur-sm'>
            <div className='p-4 rounded-full bg-white/10 backdrop-blur-sm border border-white/20'>
              <span className='text-4xl'>🏪</span>
            </div>
          </div>
        </div>
        
        <h1 
          className='text-5xl md:text-7xl font-bold mb-6 text-white drop-shadow-2xl leading-tight'
          style={{ fontFamily: storeTheme?.fontFamily }}
        >
          Welcome to
          <br />
          <span className='bg-gradient-to-r from-white via-gray-100 to-gray-300 bg-clip-text text-transparent'>
            {storeInfo?.businessName}
          </span>
        </h1>
        
        <p className='text-xl md:text-2xl text-gray-200 mb-12 max-w-2xl mx-auto drop-shadow-lg font-light'>
          {storeInfo?.businessDescription || 'Experience premium shopping like never before'}
        </p>
        
        <div className='flex flex-col sm:flex-row gap-6 justify-center items-center'>
          <ThemeButton
            onClick={handleEnterStore}
            variant='primary'
            theme={storeTheme}
            className='shadow-2xl hover:shadow-3xl'
          >
            ✨ Start Shopping
          </ThemeButton>
          
          <ThemeButton
            onClick={() => setShowNavigation(true)}
            variant='outline'
            theme={storeTheme}
          >
            📖 Our Story
          </ThemeButton>
        </div>
        
        <div className='mt-16 grid grid-cols-1 md:grid-cols-3 gap-8'>
          <div className='text-white backdrop-blur-sm bg-white/10 p-6 rounded-2xl border border-white/20'>
            <div className='text-3xl mb-3'>🚚</div>
            <h3 className='font-bold text-lg mb-2'>Fast Delivery</h3>
            <p className='text-gray-200 text-sm'>Same day delivery available</p>
          </div>
          
          <div className='text-white backdrop-blur-sm bg-white/10 p-6 rounded-2xl border border-white/20'>
            <div className='text-3xl mb-3'>⭐</div>
            <h3 className='font-bold text-lg mb-2'>Premium Quality</h3>
            <p className='text-gray-200 text-sm'>Curated selection of products</p>
          </div>
          
          <div className='text-white backdrop-blur-sm bg-white/10 p-6 rounded-2xl border border-white/20'>
            <div className='text-3xl mb-3'>💳</div>
            <h3 className='font-bold text-lg mb-2'>Secure Payment</h3>
            <p className='text-gray-200 text-sm'>100% secure transactions</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function GlassMorphismLayout({ storeInfo, storeTheme, storeBanner, getBannerUrl, handleEnterStore, setShowNavigation }) {
  const bannerUrl = getBannerUrl();
  const bannerColor = storeBanner?.type === 'color' ? storeBanner.color : storeTheme?.primaryColor || '#4f46e5';

  return (
    <div className='relative min-h-screen overflow-hidden'>
      {bannerUrl ? (
        <img
          src={bannerUrl}
          alt='Store Banner'
          className='absolute inset-0 w-full h-full object-cover'
        />
      ) : (
        <div 
          className='absolute inset-0 w-full h-full'
          style={{ backgroundColor: bannerColor }}
        />
      )}
      
      <div className='absolute inset-0 bg-gradient-to-br from-black/60 via-black/40 to-transparent backdrop-blur-[2px]' />
      
      <div className='relative z-10 min-h-screen flex items-center justify-center p-8'>
        <div className='backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-8 md:p-12 max-w-4xl w-full shadow-2xl'>
          <div className='flex flex-col md:flex-row items-center gap-8'>
            <div className='flex-shrink-0'>
              <div className='w-32 h-32 rounded-full bg-gradient-to-br from-white/30 to-transparent border border-white/40 flex items-center justify-center'>
                <span className='text-5xl'>🏪</span>
              </div>
            </div>
            
            <div className='flex-1'>
              <h1 
                className='text-4xl md:text-5xl font-bold mb-4 text-white'
                style={{ fontFamily: storeTheme?.fontFamily }}
              >
                {storeInfo?.businessName}
              </h1>
              
              <p className='text-lg text-gray-200 mb-8'>
                {storeInfo?.businessDescription || 'Modern shopping experience with glass design aesthetics'}
              </p>
              
              <div className='flex flex-wrap gap-4'>
                <ThemeButton
                  onClick={handleEnterStore}
                  variant='outline'
                  theme={storeTheme}
                  className='backdrop-blur-sm'
                >
                  🛒 Browse Collection
                </ThemeButton>
                
                <ThemeButton
                  onClick={() => setShowNavigation(true)}
                  variant='secondary'
                  theme={storeTheme}
                >
                  📱 Contact Us
                </ThemeButton>
              </div>
            </div>
          </div>
          
          <div className='mt-12 pt-8 border-t border-white/20'>
            <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
              {['Premium Brands', '24/7 Support', 'Easy Returns', 'Free Shipping'].map((feature, idx) => (
                <div 
                  key={idx}
                  className='text-center p-4 rounded-xl backdrop-blur-sm bg-white/5 border border-white/10 hover:bg-white/10 transition-colors'
                >
                  <div className='text-2xl mb-2'>
                    {['💎', '🛡️', '↩️', '🚚'][idx]}
                  </div>
                  <p className='text-white text-sm font-medium'>{feature}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ParallaxLayout({ storeInfo, storeTheme, handleEnterStore }) {
  return (
    <div className='relative min-h-screen overflow-hidden'>
      {/* Background layers */}
      <div className='absolute inset-0 bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900' />
      
      {/* Parallax layers */}
      <div className='absolute inset-0 opacity-30'>
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className='absolute rounded-full bg-white/10'
            style={{
              width: `${100 + i * 50}px`,
              height: `${100 + i * 50}px`,
              left: `${10 + i * 15}%`,
              top: `${20 + i * 10}%`,
              transform: `translateY(${i * 10}px)`,
            }}
          />
        ))}
      </div>
      
      <div className='relative z-10 min-h-screen flex items-center justify-center p-8'>
        <div className='text-center max-w-4xl'>
          <div className='mb-8 animate-bounce-slow'>
            <span className='text-6xl'>🚀</span>
          </div>
          
          <h1 
            className='text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-white via-purple-200 to-pink-200 bg-clip-text text-transparent'
            style={{ fontFamily: storeTheme?.fontFamily }}
          >
            {storeInfo?.businessName}
          </h1>
          
          <p className='text-xl text-gray-300 mb-12 max-w-2xl mx-auto'>
            {storeInfo?.businessDescription || 'Launch into a world of amazing products and services'}
          </p>
          
          <div className='space-y-6'>
            <ThemeButton
              onClick={handleEnterStore}
              variant='primary'
              theme={storeTheme}
              className='animate-pulse'
            >
              🚀 Launch Store
            </ThemeButton>
            
            <div className='flex flex-wrap justify-center gap-4'>
              <button className='text-gray-300 hover:text-white transition-colors'>
                ⚡ Fast Delivery
              </button>
              <button className='text-gray-300 hover:text-white transition-colors'>
                🔒 Secure Checkout
              </button>
              <button className='text-gray-300 hover:text-white transition-colors'>
                💎 Premium Quality
              </button>
            </div>
          </div>
          
          <div className='mt-16 grid grid-cols-1 md:grid-cols-3 gap-8'>
            {[
              { icon: '🎯', title: 'Targeted', desc: 'Perfect for your needs' },
              { icon: '⚡', title: 'Fast', desc: 'Lightning quick service' },
              { icon: '🔮', title: 'Future', desc: 'Ahead of the curve' }
            ].map((item, idx) => (
              <div 
                key={idx}
                className='backdrop-blur-sm bg-white/5 p-6 rounded-2xl border border-white/10 hover:scale-105 transition-transform'
              >
                <div className='text-4xl mb-4'>{item.icon}</div>
                <h3 className='font-bold text-white mb-2'>{item.title}</h3>
                <p className='text-gray-300 text-sm'>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProductShowcaseLayout({ storeInfo, storeTheme, handleEnterStore }) {
  const products = [
    { name: 'Premium Headphones', price: '$199', image: '🎧' },
    { name: 'Smart Watch', price: '$299', image: '⌚' },
    { name: 'Wireless Speaker', price: '$149', image: '🔊' },
  ];

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-900 to-gray-800'>
      {/* Header */}
      <div className='relative h-96 overflow-hidden'>
        <div className='absolute inset-0 bg-gradient-to-r from-indigo-600 to-purple-600' />
        <div className='absolute inset-0 bg-black/20' />
        
        <div className='relative h-full flex items-center justify-center p-8'>
          <div className='text-center'>
            <h1 
              className='text-5xl md:text-6xl font-bold text-white mb-4'
              style={{ fontFamily: storeTheme?.fontFamily }}
            >
              {storeInfo?.businessName}
            </h1>
            <p className='text-xl text-gray-200 mb-8'>
              Discover amazing products curated just for you
            </p>
          </div>
        </div>
      </div>
      
      {/* Product Showcase */}
      <div className='max-w-7xl mx-auto px-4 py-12 -mt-24 relative z-10'>
        <div className='grid grid-cols-1 md:grid-cols-3 gap-8'>
          {products.map((product, idx) => (
            <div 
              key={idx}
              className='bg-white rounded-2xl p-8 shadow-2xl hover:shadow-3xl transition-all hover:-translate-y-2'
            >
              <div className='text-6xl text-center mb-6'>{product.image}</div>
              <h3 className='text-xl font-bold text-center mb-2'>{product.name}</h3>
              <p className='text-gray-600 text-center mb-4'>Only {product.price}</p>
              <button 
                onClick={handleEnterStore}
                className='w-full py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors'
              >
                View Details
              </button>
            </div>
          ))}
        </div>
        
        {/* CTA Section */}
        <div className='mt-16 text-center'>
          <h2 className='text-3xl font-bold text-white mb-4'>
            Ready to Explore More?
          </h2>
          <p className='text-gray-300 mb-8 max-w-2xl mx-auto'>
            Browse our complete collection of premium products with exclusive offers
          </p>
          <ThemeButton
            onClick={handleEnterStore}
            variant='primary'
            theme={storeTheme}
            className='text-lg'
          >
            🛍️ View All Products
          </ThemeButton>
        </div>
        
        {/* Features */}
        <div className='mt-16 grid grid-cols-1 md:grid-cols-4 gap-6'>
          {[
            { icon: '🚚', text: 'Free Shipping' },
            { icon: '↩️', text: '30-Day Returns' },
            { icon: '🛡️', text: '2-Year Warranty' },
            { icon: '💳', text: 'Secure Payment' }
          ].map((feature, idx) => (
            <div 
              key={idx}
              className='bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center border border-white/20'
            >
              <div className='text-2xl mb-2'>{feature.icon}</div>
              <p className='text-white text-sm'>{feature.text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DarkMinimalLayout({ storeInfo, storeTheme, handleEnterStore, setShowNavigation }) {
  return (
    <div className='min-h-screen bg-gray-900'>
      {/* Navigation */}
      <div className='p-6'>
        <div className='max-w-7xl mx-auto flex justify-between items-center'>
          <h1 
            className='text-2xl font-bold text-white'
            style={{ fontFamily: storeTheme?.fontFamily }}
          >
            {storeInfo?.businessName}
          </h1>
          <button
            onClick={handleEnterStore}
            className='px-6 py-2 bg-white text-gray-900 rounded-lg font-semibold hover:bg-gray-100 transition-colors'
          >
            Enter
          </button>
        </div>
      </div>
      
      {/* Hero Section */}
      <div className='max-w-4xl mx-auto px-4 py-24 text-center'>
        <div className='inline-block p-3 bg-white/10 rounded-full mb-8'>
          <span className='text-4xl'>🌙</span>
        </div>
        
        <h1 
          className='text-5xl md:text-7xl font-bold text-white mb-6 leading-tight'
          style={{ fontFamily: storeTheme?.fontFamily }}
        >
          Minimal.
          <br />
          Elegant.
          <br />
          <span className='text-gray-400'>Essential.</span>
        </h1>
        
        <p className='text-xl text-gray-400 mb-12 max-w-2xl mx-auto'>
          {storeInfo?.businessDescription || 'The essence of modern shopping experience'}
        </p>
        
        <div className='flex flex-col sm:flex-row gap-4 justify-center'>
          <button
            onClick={handleEnterStore}
            className='px-8 py-4 bg-white text-gray-900 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors flex items-center justify-center gap-2'
          >
            <span>Explore</span>
            <span>→</span>
          </button>
          
          <button
            onClick={() => setShowNavigation(true)}
            className='px-8 py-4 border border-gray-700 text-gray-300 rounded-lg font-bold text-lg hover:bg-gray-800 transition-colors'
          >
            Learn More
          </button>
        </div>
        
        {/* Scroll Indicator */}
        <div className='mt-24'>
          <div className='text-gray-500 text-sm mb-2'>Scroll</div>
          <div className='w-6 h-10 border-2 border-gray-700 rounded-full mx-auto flex justify-center'>
            <div className='w-1 h-3 bg-gray-500 rounded-full mt-2 animate-bounce'></div>
          </div>
        </div>
      </div>
      
      {/* Features Section */}
      <div className='max-w-6xl mx-auto px-4 pb-24'>
        <div className='grid grid-cols-1 md:grid-cols-3 gap-8'>
          {[
            { title: 'Simple', desc: 'Clean and intuitive interface', icon: '⚡' },
            { title: 'Fast', desc: 'Lightning quick performance', icon: '🚀' },
            { title: 'Secure', desc: 'Protected transactions', icon: '🔒' }
          ].map((feature, idx) => (
            <div 
              key={idx}
              className='p-6 border border-gray-800 rounded-xl hover:border-gray-700 transition-colors'
            >
              <div className='text-3xl mb-4'>{feature.icon}</div>
              <h3 className='text-xl font-bold text-white mb-2'>{feature.title}</h3>
              <p className='text-gray-400'>{feature.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Main Landing Page Component
export default function LandingPage({ onEnterStore, onBackToLanding }) {
const { businessName: urlBusinessName, bussinessUuid } = useParams();
  const navigate = useNavigate();
  
  const [storeInfo, setStoreInfo] = useState(null);
  const [storeTheme, setStoreTheme] = useState(null);
  const [storeBanner, setStoreBanner] = useState(null);
  const [layoutSettings, setLayoutSettings] = useState(null);
  const [showLayoutSelector, setShowLayoutSelector] = useState(false);
  const [showNavigation, setShowNavigation] = useState(false);
  const [loading, setLoading] = useState(true);
  const [redirectTimer, setRedirectTimer] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [activeCarouselIndex, setActiveCarouselIndex] = useState(0);
  const businessData = useSelector((state) => state.bussiness.business);
  const business = Array.isArray(businessData)
    ? businessData.find((b) => b.bussinessUuid === bussinessUuid) || businessData[0]
    : businessData;
  const displayBusinessName = urlBusinessName || business?.businessName || "Store";

  // Carousel images
  const carouselImages = [
    'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=1600&auto=format',
    'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=1600&auto=format',
    'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=1600&auto=format'
  ];

  // Auto rotate carousel
  useEffect(() => {
    if (layoutSettings?.type === 'carousel') {
      const interval = setInterval(() => {
        setActiveCarouselIndex(prev => (prev + 1) % carouselImages.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [layoutSettings?.type]);
useEffect(() => {
  initTracking(bussinessUuid);
  trackPageView("landing");
}, []);

  // Load store data and settings
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Load business info
        const businessRes = await getBusiness(bussinessUuid);
        if (businessRes.data) {
          setStoreInfo(businessRes.data);
        }
        
        // Load store settings including layout
        const settingsRes = await storeSettingsService.getPublicStoreSettings(bussinessUuid);
        if (settingsRes.success) {
          const settings = settingsRes.data;
          setStoreTheme(settings.theme);
          setStoreBanner(settings.banner);
          setLayoutSettings(settings.layout || {
            type: 'fullscreen-banner',
            showLanding: true,
            layoutSettings: {
              showNavigation: true,
              autoRedirect: false,
              redirectDelay: 0
            }
          });
        } else {
          // Default settings
          setStoreTheme({
            themeName: 'Modern Indigo',
            fontFamily: 'Inter',
            primaryColor: '#4f46e5',
            secondaryColor: '#10b981',
            backgroundColor: '#ffffff',
            buttonColor: '#4f46e5',
            textColor: '#1f2937',
            headingColor: '#111827',
            borderRadius: '12px',
            buttonStyle: 'rounded',
            cardStyle: 'shadow'
          });
          
          setLayoutSettings({
            type: 'fullscreen-banner',
            showLanding: true,
            layoutSettings: {
              showNavigation: true,
              autoRedirect: false,
              redirectDelay: 0
            }
          });
        }
      } catch (error) {
        console.error('Error loading landing page data:', error);
        toast.error('Failed to load store settings');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [bussinessUuid]);

  // Handle auto-redirect if enabled
  useEffect(() => {
    if (layoutSettings?.layoutSettings?.autoRedirect && layoutSettings?.layoutSettings?.redirectDelay > 0) {
      const delay = layoutSettings.layoutSettings.redirectDelay;
      setCountdown(delay);
      
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            handleEnterStore();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      setRedirectTimer(timer);
      
      return () => {
        if (timer) clearInterval(timer);
      };
    }
  }, [layoutSettings]);

  const handleEnterStore = () => {
    trackIntent("B2C");
    if (redirectTimer) {
      clearInterval(redirectTimer);
      setRedirectTimer(null);
    }
    
    if (onEnterStore) {
      onEnterStore();
    } else {
      navigate(`/${displayBusinessName}/store/${bussinessUuid}`);
    }
  };
 const handleEnterStoreAsB2B = () => {
  trackIntent("B2B");
    localStorage.setItem("b2b_intent", "true");
    navigate(`/${displayBusinessName}/store/${bussinessUuid}`);
  };
  const handleBackToLanding = () => {
    if (onBackToLanding) {
      onBackToLanding();
    } else {
      navigate(`/${displayBusinessName}/store/${bussinessUuid}/landing`);
    }
  };

  const handleSelectLayout = async (layoutId) => {
    try {
      const response = await storeSettingsService.updateLayout(bussinessUuid, {
        ...layoutSettings,
        type: layoutId
      });
      
      if (response.success) {
        setLayoutSettings(prev => ({ ...prev, type: layoutId }));
        toast.success('Layout updated successfully');
      } else {
        toast.error(response.error || 'Failed to update layout');
      }
    } catch (error) {
      console.error('Error updating layout:', error);
      toast.error('Failed to update layout');
    } finally {
      setShowLayoutSelector(false);
    }
  };

  const toggleLandingPage = async () => {
    try {
      const newShowLanding = !layoutSettings.showLanding;
      const response = await storeSettingsService.updateLayoutSetting(
        bussinessUuid,
        'showLanding',
        newShowLanding
      );
      
      if (response.success) {
        setLayoutSettings(prev => ({ ...prev, showLanding: newShowLanding }));
        toast.success(`Landing page ${newShowLanding ? 'enabled' : 'disabled'}`);
      }
    } catch (error) {
      console.error('Error toggling landing page:', error);
      toast.error('Failed to update landing page setting');
    }
  };

// Function to get banner URL - UPDATED
const getBannerUrl = () => {
  console.log("storeBanner:", storeBanner); // Debug
  console.log("storeInfo?.bannerImage:", storeInfo?.bannerImage); // Debug
  
  // Check storeBanner first (from settings)
  if (storeBanner) {
    if (storeBanner.type === 'color') {
      return null;
    } else if (storeBanner.type === 'image' && storeBanner.imageUrl) {
      let url = storeBanner.imageUrl;
      
      // If it's a relative S3 path, construct full URL
      if (url && !url.startsWith('http') && !url.startsWith('https') && url.includes('/')) {
        // It's a S3 object key, construct the full S3 URL
        url = `https://dukanbuddy-normal.s3.us-east-1.amazonaws.com/${url}`;
      }
      
      console.log("Using storeBanner URL:", url);
      return url;
    }
  }
  
  // Fallback to business data bannerImage
  if (storeInfo?.bannerImage) {
    let url = storeInfo.bannerImage;
    if (url && !url.startsWith('http') && !url.startsWith('https')) {
      if (url.startsWith('/')) {
        url = `${window.location.origin}${url}`;
      } else {
        // Check if it's an AWS S3 key
        if (url.includes('/')) {
          url = `https://dukanbuddy-normal.s3.us-east-1.amazonaws.com/${url}`;
        } else {
          url = `${AWS_URL}${url}`;
        }
      }
    }
    console.log("Using storeInfo bannerImage:", url);
    return url;
  }
  

};
  const renderLayout = () => {
    const bannerUrl = getBannerUrl();
    const bannerColor = storeBanner?.type === 'color' ? storeBanner.color : storeTheme?.primaryColor || '#4f46e5';
    const currentLayout = layoutSettings?.type || 'fullscreen-banner';
    
    const featuredSections = layoutSettings?.layoutSettings?.featuredSections || [
      {
        title: 'Premium Products',
        description: 'Carefully curated selection',
        icon: '📦',
        enabled: true
      },
      {
        title: 'Fast Delivery',
        description: 'Quick and reliable shipping',
        icon: '🚚',
        enabled: true
      },
      {
        title: 'Best Quality',
        description: 'Top-notch customer satisfaction',
        icon: '⭐',
        enabled: true
      }
    ];

    switch (currentLayout) {
      case 'fullscreen-banner':
        return (
          <div className='relative min-h-screen flex items-center justify-center overflow-hidden'>
            {/* Background */}
            {bannerUrl ? (
              <img
                src={bannerUrl}
                alt='Store Banner'
                className='absolute inset-0 w-full h-full object-cover'
              />
            ) : (
              <div 
                className='absolute inset-0 w-full h-full'
                style={{ backgroundColor: bannerColor }}
              />
            )}
            
            {/* Overlay */}
            <div className='absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent' />
            
            {/* Auto-redirect Countdown */}
            {countdown > 0 && (
              <div className='absolute top-4 right-4 z-20'>
                <div className='backdrop-blur-sm bg-white/20 border border-white/30 rounded-full px-4 py-2 text-white font-medium'>
                  Auto redirect in {countdown}s
                  <button 
                    onClick={() => {
                      if (redirectTimer) clearInterval(redirectTimer);
                      setRedirectTimer(null);
                      setCountdown(0);
                    }}
                    className='ml-2 text-sm underline'
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            
            {/* Content */}
            <div className='relative z-10 text-center px-4 max-w-4xl mx-auto'>
              <h1 
                className='text-5xl md:text-7xl font-bold mb-6 text-white drop-shadow-2xl'
                style={{ fontFamily: storeTheme?.fontFamily }}
              >
                Welcome<br />
                <span className='bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent'>
                  {storeInfo?.businessName || 'Our Store'}
                </span>
              </h1>
              
              <p className='text-xl md:text-2xl text-gray-200 mb-10 max-w-2xl mx-auto drop-shadow-lg'>
                {storeInfo?.businessDescription || 'Discover amazing products and services tailored just for you'}
              </p>
              
              <div className='flex flex-col sm:flex-row gap-4 justify-center items-center'>
                <ThemeButton
                            onClick={() => {
    localStorage.removeItem("b2b_intent");
    localStorage.removeItem("force_b2b_login");
    localStorage.setItem("b2b_Price", "false");
    handleEnterStore();
  }}
                  variant='primary'
                  theme={storeTheme}
                  className='animate-bounce-slow'
                >
                  🛍️ Shop Now
                </ThemeButton>
                <ThemeButton
                  onClick={handleEnterStoreAsB2B}
                  variant='primary'
                  theme={storeTheme}
                  className='animate-bounce-slow'
                >
                  🛍️ Bulk Order
                </ThemeButton>
                
                {layoutSettings?.layoutSettings?.showNavigation && (
                  <ThemeButton
                    onClick={() => setShowNavigation(true)}
                    variant='outline'
                    theme={storeTheme}
                  >
                    📖 Learn More
                  </ThemeButton>
                )}
              </div>
              
              {/* Featured Sections */}
              {featuredSections.some(section => section.enabled) && (
                <div className='mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 text-white'>
                  {featuredSections
                    .filter(section => section.enabled)
                    .map((section, index) => (
                      <div 
                        key={index}
                        className='backdrop-blur-sm bg-white/10 p-6 rounded-2xl border border-white/20 hover:bg-white/20 transition-colors'
                      >
                        <div className='text-3xl mb-2'>{section.icon}</div>
                        <h3 className='font-bold text-lg mb-2'>{section.title}</h3>
                        <p className='text-gray-200'>{section.description}</p>
                      </div>
                    ))}
                </div>
              )}
              
              {/* Navigation Hint */}
              {/* {layoutSettings?.layoutSettings?.showNavigation && (
                <div className='mt-12'>
                  <button
                    onClick={() => setShowNavigation(true)}
                    className='text-white/80 hover:text-white flex items-center gap-2 mx-auto text-sm animate-pulse'
                  >
                    <span>More options</span>
                    <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
                    </svg>
                  </button>
                </div>
              )} */}
            </div>
            
            {/* Floating Back Button (for returning from store) */}
            {onBackToLanding && (
              <button
                onClick={handleBackToLanding}
                className='fixed top-4 left-4 z-20 p-3 rounded-full backdrop-blur-sm bg-white/10 border border-white/20 text-white hover:bg-white/20 transition-all'
                title='Back to Landing Page'
              >
                <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M10 19l-7-7m0 0l7-7m-7 7h18' />
                </svg>
              </button>
            )}
          </div>
        );

      case 'split-screen':
        return (
          <div className='min-h-screen flex flex-col md:flex-row'>
            {/* Banner Side */}
            <div className='flex-1 relative'>
              {bannerUrl ? (
                <img
                  src={bannerUrl}
                  alt='Store Banner'
                  className='absolute inset-0 w-full h-full object-cover'
                />
              ) : (
                <div 
                  className='absolute inset-0 w-full h-full'
                  style={{ backgroundColor: bannerColor }}
                />
              )}
              <div className='absolute inset-0 bg-gradient-to-r from-black/50 to-transparent' />
            </div>
            
            {/* Content Side */}
            <div className='flex-1 flex items-center justify-center p-8' style={{ backgroundColor: storeTheme?.backgroundColor || '#ffffff' }}>
              <div className='max-w-lg'>
                <h1 
                  className='text-4xl md:text-5xl font-bold mb-6'
                  style={{ color: storeTheme?.headingColor || '#111827', fontFamily: storeTheme?.fontFamily }}
                >
                  Welcome <br />
                  <span style={{ color: storeTheme?.primaryColor || '#4f46e5' }}>
                    {storeInfo?.businessName}
                  </span>
                </h1>
                
                <p className='text-lg mb-8' style={{ color: storeTheme?.textColor || '#1f2937' }}>
                  {storeInfo?.businessDescription || 'Step into a world of quality products and exceptional service.'}
                </p>
                
                <div className='space-y-4'>
                  <ThemeButton
                    onClick={handleEnterStore}
                    variant='primary'
                    theme={storeTheme}
                    className='w-full'
                  >
                    🛍️ Explore Store
                  </ThemeButton>
                  
                  {layoutSettings?.layoutSettings?.showNavigation && (
                    <div className='flex gap-4'>
                      <ThemeButton
                        onClick={() => setShowNavigation(true)}
                        variant='secondary'
                        theme={storeTheme}
                        className='flex-1'
                      >
                        📞 Contact
                      </ThemeButton>
                      
                      <ThemeButton
                        onClick={() => setShowNavigation(true)}
                        variant='outline'
                        theme={storeTheme}
                        className='flex-1'
                      >
                        ℹ️ About
                      </ThemeButton>
                    </div>
                  )}
                </div>
                
                <div className='mt-8 pt-8 border-t' style={{ borderColor: storeTheme?.primaryColor + '20' || '#e5e7eb' }}>
                  <p className='text-sm' style={{ color: storeTheme?.textColor + '80' || '#6b7280' }}>
                    {storeInfo?.address || 'Visit our store for the best experience'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'minimal':
        return (
          <div className='min-h-screen'>
            {/* Minimal Header Banner */}
            <div className='relative h-64'>
              {bannerUrl ? (
                <img
                  src={bannerUrl}
                  alt='Store Banner'
                  className='absolute inset-0 w-full h-full object-cover'
                />
              ) : (
                <div 
                  className='absolute inset-0 w-full h-full'
                  style={{ backgroundColor: bannerColor }}
                />
              )}
              <div className='absolute inset-0 bg-gradient-to-b from-black/30 to-transparent' />
              
              {/* Logo/Brand */}
              <div className='absolute bottom-4 left-4'>
                <h1 
                  className='text-2xl font-bold text-white'
                  style={{ fontFamily: storeTheme?.fontFamily }}
                >
                  {storeInfo?.businessName}
                </h1>
                <p className='text-white/80 text-sm'>{storeInfo?.businessCategory}</p>
              </div>
            </div>
            
            {/* Quick Navigation */}
            <div className='max-w-4xl mx-auto px-4 py-8'>
              <div className='text-center mb-8'>
                <h2 className='text-3xl font-bold mb-4' style={{ color: storeTheme?.headingColor, fontFamily: storeTheme?.fontFamily }}>
                  Welcome
                </h2>
                <p className='text-lg' style={{ color: storeTheme?.textColor }}>
                  {storeInfo?.businessDescription || 'Ready to explore our collection?'}
                </p>
              </div>
              
              <div className='grid grid-cols-1 md:grid-cols-3 gap-6 mb-8'>
                <div className='p-6 rounded-xl border text-center hover:shadow-lg transition-shadow cursor-pointer'
                     onClick={handleEnterStore}
                     style={{ borderColor: storeTheme?.primaryColor + '30' || '#e5e7eb' }}>
                  <div className='text-4xl mb-3'>🛍️</div>
                  <h3 className='font-bold mb-2' style={{ color: storeTheme?.headingColor }}>Shop Products</h3>
                  <p className='text-sm' style={{ color: storeTheme?.textColor + '80' }}>Browse our catalog</p>
                </div>
                
                {layoutSettings?.layoutSettings?.showNavigation && (
                  <>
                    <div className='p-6 rounded-xl border text-center hover:shadow-lg transition-shadow cursor-pointer'
                         onClick={() => setShowNavigation(true)}
                         style={{ borderColor: storeTheme?.primaryColor + '30' || '#e5e7eb' }}>
                      <div className='text-4xl mb-3'>📞</div>
                      <h3 className='font-bold mb-2' style={{ color: storeTheme?.headingColor }}>Contact Us</h3>
                      <p className='text-sm' style={{ color: storeTheme?.textColor + '80' }}>Get in touch</p>
                    </div>
                    
                    <div className='p-6 rounded-xl border text-center hover:shadow-lg transition-shadow cursor-pointer'
                         onClick={() => setShowNavigation(true)}
                         style={{ borderColor: storeTheme?.primaryColor + '30' || '#e5e7eb' }}>
                      <div className='text-4xl mb-3'>📍</div>
                      <h3 className='font-bold mb-2' style={{ color: storeTheme?.headingColor }}>Visit Store</h3>
                      <p className='text-sm' style={{ color: storeTheme?.textColor + '80' }}>Find our location</p>
                    </div>
                  </>
                )}
              </div>
              
              <div className='text-center'>
                <ThemeButton
                  onClick={handleEnterStore}
                  variant='primary'
                  theme={storeTheme}
                >
                  Enter Store
                </ThemeButton>
              </div>
            </div>
          </div>
        );

      case 'carousel':
        return (
          <div className='relative min-h-screen overflow-hidden'>
            {/* Carousel */}
            <div className='absolute inset-0'>
              <img
                src={carouselImages[activeCarouselIndex]}
                alt='Carousel Banner'
                className='absolute inset-0 w-full h-full object-cover transition-opacity duration-1000'
              />
              <div className='absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent' />
            </div>
            
            {/* Carousel Controls */}
            <div className='absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-2 z-20'>
              {carouselImages.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveCarouselIndex(idx)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    idx === activeCarouselIndex ? 'bg-white w-8' : 'bg-white/50 hover:bg-white/80'
                  }`}
                />
              ))}
            </div>
            
            {/* Content */}
            <div className='relative z-10 min-h-screen flex items-center justify-center p-8'>
              <div className='text-center max-w-4xl'>
                <h1 
                  className='text-5xl md:text-7xl font-bold mb-6 text-white drop-shadow-2xl'
                  style={{ fontFamily: storeTheme?.fontFamily }}
                >
                  {storeInfo?.businessName}
                </h1>
                
                <p className='text-xl text-gray-200 mb-10 max-w-2xl mx-auto drop-shadow-lg'>
                  {storeInfo?.businessDescription || 'Discover something new every time you visit'}
                </p>
                
                <div className='flex flex-col sm:flex-row gap-4 justify-center'>
                  <ThemeButton
                    onClick={handleEnterStore}
                    variant='primary'
                    theme={storeTheme}
                  >
                    🛒 Shop Now
                  </ThemeButton>
                  
                  <ThemeButton
                    onClick={() => setActiveCarouselIndex((prev) => (prev + 1) % carouselImages.length)}
                    variant='outline'
                    theme={storeTheme}
                  >
                    🔄 Next Slide
                  </ThemeButton>
                </div>
              </div>
            </div>
          </div>
        );

      case 'gradient':
        return (
          <div className='relative min-h-screen overflow-hidden'>
            {/* Gradient Background */}
            <div 
              className='absolute inset-0 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 animate-gradient-x'
            />
            
            {/* Content */}
            <div className='relative z-10 min-h-screen flex items-center justify-center p-8'>
              <div className='text-center max-w-4xl'>
                <h1 
                  className='text-5xl md:text-7xl font-bold mb-6 text-white drop-shadow-2xl'
                  style={{ fontFamily: storeTheme?.fontFamily }}
                >
                  <span className='bg-gradient-to-r from-white via-cyan-200 to-blue-200 bg-clip-text text-transparent'>
                    {storeInfo?.businessName}
                  </span>
                </h1>
                
                <p className='text-xl text-gray-200 mb-10 max-w-2xl mx-auto drop-shadow-lg'>
                  {storeInfo?.businessDescription || 'Experience the gradient of quality and style'}
                </p>
                
                <div className='flex flex-wrap justify-center gap-4'>
                  <ThemeButton
                    onClick={handleEnterStore}
                    variant='secondary'
                    theme={storeTheme}
                    className='shadow-2xl'
                  >
                    🌈 Enter Store
                  </ThemeButton>
                  
                  <ThemeButton
                    onClick={() => setShowNavigation(true)}
                    variant='outline'
                    theme={storeTheme}
                  >
                    ✨ Features
                  </ThemeButton>
                </div>
                
                {/* Gradient Elements */}
                <div className='mt-16 flex justify-center gap-8'>
                  {['🔮', '💎', '✨'].map((icon, idx) => (
                    <div
                      key={idx}
                      className='text-4xl animate-bounce'
                      style={{ animationDelay: `${idx * 200}ms` }}
                    >
                      {icon}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 'centered':
        return <CenteredElegantLayout 
          storeInfo={storeInfo}
          storeTheme={storeTheme}
          storeBanner={storeBanner}
          getBannerUrl={getBannerUrl}
          handleEnterStore={handleEnterStore}
          setShowNavigation={setShowNavigation}
        />;

      case 'glass-morphism':
        return <GlassMorphismLayout 
          storeInfo={storeInfo}
          storeTheme={storeTheme}
          storeBanner={storeBanner}
          getBannerUrl={getBannerUrl}
          handleEnterStore={handleEnterStore}
          setShowNavigation={setShowNavigation}
        />;

      case 'parallax':
        return <ParallaxLayout 
          storeInfo={storeInfo}
          storeTheme={storeTheme}
          handleEnterStore={handleEnterStore}
        />;

      case 'product-showcase':
        return <ProductShowcaseLayout 
          storeInfo={storeInfo}
          storeTheme={storeTheme}
          handleEnterStore={handleEnterStore}
        />;

      case 'dark-minimal':
        return <DarkMinimalLayout 
          storeInfo={storeInfo}
          storeTheme={storeTheme}
          handleEnterStore={handleEnterStore}
          setShowNavigation={setShowNavigation}
        />;

      case 'animated':
        return <EnhancedAnimatedLayout 
          storeInfo={storeInfo}
          storeTheme={storeTheme}
          handleEnterStore={handleEnterStore}
          setShowNavigation={setShowNavigation}
        />;

      case 'sidebar':
        return (
          <div className="min-h-screen flex">
            {/* Sidebar */}
            <div className="w-64 bg-gradient-to-b from-gray-900 to-gray-800 text-white p-6">
              <h1 className="text-2xl font-bold mb-8" style={{ fontFamily: storeTheme?.fontFamily }}>
                {storeInfo?.businessName}
              </h1>
              <nav className="space-y-4">
                <button onClick={handleEnterStore} className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/10 w-full text-left">
                  <span className="text-xl">🛍️</span>
                  <span>Shop Now</span>
                </button>
                <button onClick={() => setShowNavigation(true)} className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/10 w-full text-left">
                  <span className="text-xl">📖</span>
                  <span>About Us</span>
                </button>
                <button onClick={() => setShowNavigation(true)} className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/10 w-full text-left">
                  <span className="text-xl">📞</span>
                  <span>Contact</span>
                </button>
              </nav>
            </div>
            
            {/* Main Content */}
            <div className="flex-1 bg-gray-100 flex items-center justify-center p-8">
              <div className="max-w-2xl">
                <h2 className="text-4xl font-bold mb-6">Welcome {storeInfo?.businessName}</h2>
                <p className="text-lg text-gray-600 mb-8">
                  {storeInfo?.businessDescription || 'Navigate through our sidebar to explore everything we offer.'}
                </p>
                <ThemeButton
                  onClick={handleEnterStore}
                  variant="primary"
                  theme={storeTheme}
                >
                  Start Shopping
                </ThemeButton>
              </div>
            </div>
          </div>
        );

      case 'card-grid':
        return (
          <div className="min-h-screen bg-gray-50 p-8">
            <div className="max-w-6xl mx-auto">
              <h1 className="text-4xl font-bold text-center mb-12">{storeInfo?.businessName}</h1>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {['Products', 'Services', 'About', 'Contact', 'Blog', 'Gallery'].map((item, idx) => (
                  <div key={idx} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                    <div className="text-4xl mb-4 text-center">
                      {['📦', '⚙️', '🏢', '📞', '📝', '🖼️'][idx]}
                    </div>
                    <h3 className="text-xl font-bold mb-2 text-center">{item}</h3>
                    <p className="text-gray-600 text-center mb-4">
                      Explore our {item.toLowerCase()} section
                    </p>
                    <button
                      onClick={item === 'Products' ? handleEnterStore : () => setShowNavigation(true)}
                      className="w-full py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                    >
                      Explore
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'masonry':
        return (
          <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8">
            <div className="max-w-6xl mx-auto">
              <h1 className="text-4xl font-bold text-center mb-12">{storeInfo?.businessName}</h1>
              <div className="columns-1 md:columns-2 lg:columns-3 gap-6">
                {[...Array(6)].map((_, idx) => (
                  <div key={idx} className={`mb-6 break-inside-avoid bg-white rounded-2xl p-6 shadow-lg 
                    ${idx % 3 === 0 ? 'h-64' : idx % 3 === 1 ? 'h-48' : 'h-56'}`}>
                    <div className="text-3xl mb-3">{['🏪', '🚀', '💎', '✨', '🌟', '🎯'][idx]}</div>
                    <h3 className="font-bold text-lg mb-2">Feature {idx + 1}</h3>
                    <p className="text-gray-600">Discover amazing features of our store</p>
                  </div>
                ))}
              </div>
              <div className="text-center mt-12">
                <ThemeButton
                  onClick={handleEnterStore}
                  variant="primary"
                  theme={storeTheme}
                >
                  View All Features
                </ThemeButton>
              </div>
            </div>
          </div>
        );

      case 'hero-sidebar':
        return (
          <div className="min-h-screen">
            {/* Hero */}
            <div className="h-96 relative" style={{ backgroundColor: bannerColor }}>
              <div className="absolute inset-0 bg-black/40" />
              <div className="relative h-full flex items-center justify-center">
                <h1 className="text-5xl font-bold text-white">{storeInfo?.businessName}</h1>
              </div>
            </div>
            
            <div className="max-w-7xl mx-auto p-8 grid grid-cols-1 lg:grid-cols-4 gap-8">
              {/* Sidebar */}
              <div className="lg:col-span-1 space-y-4">
                <div className="bg-white p-6 rounded-2xl shadow-lg">
                  <h3 className="font-bold text-lg mb-4">Quick Links</h3>
                  <nav className="space-y-2">
                    <button onClick={handleEnterStore} className="block w-full text-left p-3 rounded-lg hover:bg-gray-100">
                      🛍️ Shop
                    </button>
                    <button onClick={() => setShowNavigation(true)} className="block w-full text-left p-3 rounded-lg hover:bg-gray-100">
                      📖 About
                    </button>
                    <button onClick={() => setShowNavigation(true)} className="block w-full text-left p-3 rounded-lg hover:bg-gray-100">
                      📞 Contact
                    </button>
                  </nav>
                </div>
              </div>
              
              {/* Main Content */}
              <div className="lg:col-span-3">
                <div className="bg-white p-8 rounded-2xl shadow-lg">
                  <h2 className="text-3xl font-bold mb-6">Welcome!</h2>
                  <p className="text-lg text-gray-600 mb-8">
                    {storeInfo?.businessDescription || 'Discover our amazing products and services.'}
                  </p>
                  <ThemeButton
                    onClick={handleEnterStore}
                    variant="primary"
                    theme={storeTheme}
                  >
                    Start Exploring
                  </ThemeButton>
                </div>
              </div>
            </div>
          </div>
        );

      case 'split-content':
        return (
          <div className="min-h-screen bg-white">
            <div className="max-w-7xl mx-auto p-8">
              <h1 className="text-4xl font-bold text-center mb-12">{storeInfo?.businessName}</h1>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                <div className="space-y-6">
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-8 rounded-2xl">
                    <h2 className="text-2xl font-bold mb-4">Our Products</h2>
                    <p className="text-gray-600 mb-6">
                      Discover our wide range of premium products tailored for you.
                    </p>
                    <ThemeButton
                      onClick={handleEnterStore}
                      variant="primary"
                      theme={storeTheme}
                    >
                      Browse Products
                    </ThemeButton>
                  </div>
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-8 rounded-2xl">
                    <h2 className="text-2xl font-bold mb-4">Our Services</h2>
                    <p className="text-gray-600 mb-6">
                      Exceptional services to enhance your shopping experience.
                    </p>
                    <ThemeButton
                      onClick={() => setShowNavigation(true)}
                      variant="secondary"
                      theme={storeTheme}
                    >
                      Learn More
                    </ThemeButton>
                  </div>
                </div>
                <div className="space-y-6">
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-8 rounded-2xl">
                    <h2 className="text-2xl font-bold mb-4">About Us</h2>
                    <p className="text-gray-600 mb-6">
                      Learn about our mission, values, and commitment to excellence.
                    </p>
                    <ThemeButton
                      onClick={() => setShowNavigation(true)}
                      variant="primary"
                      theme={storeTheme}
                    >
                      Our Story
                    </ThemeButton>
                  </div>
                  <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-8 rounded-2xl">
                    <h2 className="text-2xl font-bold mb-4">Contact</h2>
                    <p className="text-gray-600 mb-6">
                      Get in touch with our team for any questions or support.
                    </p>
                    <ThemeButton
                      onClick={() => setShowNavigation(true)}
                      variant="secondary"
                      theme={storeTheme}
                    >
                      Contact Us
                    </ThemeButton>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'floating-elements':
        return (
          <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-blue-50 to-purple-50">
            {/* Floating elements */}
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="absolute rounded-full bg-gradient-to-r from-blue-200/30 to-purple-200/30 animate-float"
                style={{
                  width: `${Math.random() * 100 + 50}px`,
                  height: `${Math.random() * 100 + 50}px`,
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${i * 2}s`,
                  animationDuration: `${Math.random() * 20 + 20}s`,
                }}
              />
            ))}
            
            <div className="relative z-10 min-h-screen flex items-center justify-center p-8">
              <div className="text-center max-w-4xl">
                <h1 className="text-5xl md:text-6xl font-bold mb-6">
                  <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    {storeInfo?.businessName}
                  </span>
                </h1>
                <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
                  {storeInfo?.businessDescription || 'Experience shopping with floating, dynamic elements'}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <ThemeButton
                    onClick={handleEnterStore}
                    variant="primary"
                    theme={storeTheme}
                    className="animate-bounce-slow"
                  >
                    🎈 Start Floating
                  </ThemeButton>
                  <ThemeButton
                    onClick={() => setShowNavigation(true)}
                    variant="outline"
                    theme={storeTheme}
                  >
                    🌟 Features
                  </ThemeButton>
                </div>
              </div>
            </div>
          </div>
        );

      case 'timeline':
        return (
          <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white p-8">
            <div className="max-w-4xl mx-auto">
              <h1 className="text-4xl font-bold text-center mb-12">{storeInfo?.businessName} Story</h1>
              
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-blue-400 to-purple-400" />
                
                {/* Timeline items */}
                {[
                  { year: '2020', title: 'Founded', desc: 'Our journey began with a vision', icon: '🚀' },
                  { year: '2021', title: 'First Store', desc: 'Opened our first physical location', icon: '🏪' },
                  { year: '2022', title: 'Online Launch', desc: 'Expanded to digital marketplace', icon: '🌐' },
                  { year: '2023', title: 'Growth', desc: 'Served 10,000+ customers', icon: '📈' },
                  { year: '2024', title: 'Today', desc: 'Continuing to innovate and serve', icon: '🎯' },
                ].map((item, idx) => (
                  <div key={idx} className={`relative mb-12 ${idx % 2 === 0 ? 'text-left' : 'text-right'}`}>
                    <div className={`w-6 h-6 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 absolute left-1/2 transform -translate-x-1/2 z-10 flex items-center justify-center text-white`}>
                      {item.icon}
                    </div>
                    <div className={`${idx % 2 === 0 ? 'ml-auto mr-8 pr-12' : 'mr-auto ml-8 pl-12'} max-w-md`}>
                      <div className="bg-white p-6 rounded-2xl shadow-lg">
                        <div className="text-sm text-blue-600 font-semibold mb-1">{item.year}</div>
                        <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                        <p className="text-gray-600">{item.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="text-center mt-12">
                <ThemeButton
                  onClick={handleEnterStore}
                  variant="primary"
                  theme={storeTheme}
                >
                  Join Our Journey
                </ThemeButton>
              </div>
            </div>
          </div>
        );

      case 'testimonial':
        return (
          <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50 p-8">
            <div className="max-w-6xl mx-auto">
              <h1 className="text-4xl font-bold text-center mb-4">{storeInfo?.businessName}</h1>
              <p className="text-lg text-gray-600 text-center mb-12">
                {storeInfo?.businessDescription || 'Trusted by thousands of satisfied customers'}
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                {[
                  { name: 'Alex Johnson', quote: 'Amazing quality and service!', role: 'Premium Customer', rating: '★★★★★' },
                  { name: 'Sarah Miller', quote: 'Best shopping experience ever!', role: 'Loyal Customer', rating: '★★★★★' },
                  { name: 'Mike Wilson', quote: 'Highly recommended to everyone!', role: 'Business Client', rating: '★★★★★' },
                ].map((testimonial, idx) => (
                  <div key={idx} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                    <div className="text-yellow-400 text-2xl mb-4">{testimonial.rating}</div>
                    <p className="text-gray-600 italic mb-6">"{testimonial.quote}"</p>
                    <div>
                      <div className="font-bold">{testimonial.name}</div>
                      <div className="text-gray-500 text-sm">{testimonial.role}</div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="text-center">
                <h2 className="text-2xl font-bold mb-6">Ready to Experience It Yourself?</h2>
                <ThemeButton
                  onClick={handleEnterStore}
                  variant="primary"
                  theme={storeTheme}
                  className="text-lg"
                >
                  🛍️ Shop Now & Join Happy Customers
                </ThemeButton>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className='min-h-screen flex items-center justify-center' style={{ backgroundColor: bannerColor }}>
            <div className='text-center text-white p-8'>
              <h1 className='text-4xl font-bold mb-4'>Welcome  {storeInfo?.businessName}</h1>
              <button
                onClick={handleEnterStore}
                className='px-8 py-3 bg-white text-gray-900 rounded-lg font-bold hover:bg-gray-100'
                style={{ borderRadius: storeTheme?.buttonStyle === 'pill' ? '9999px' : 
                        storeTheme?.buttonStyle === 'rounded' ? '12px' : '4px' }}
              >
                Enter Store
              </button>
            </div>
          </div>
        );
    }
  };

  // Navigation Modal
  const NavigationModal = () => {
    if (!showNavigation) return null;

    return (
      <div className='fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4'>
        <div 
          className='bg-white rounded-2xl w-full max-w-lg overflow-hidden'
          style={{ fontFamily: storeTheme?.fontFamily }}
        >
          <div className='p-6 border-b'>
            <div className='flex items-center justify-between'>
              <h3 className='text-xl font-bold' style={{ color: storeTheme?.headingColor }}>
                {storeInfo?.businessName}
              </h3>
              <button
                onClick={() => setShowNavigation(false)}
                className='p-2 hover:bg-gray-100 rounded-full'
              >
                ✕
              </button>
            </div>
          </div>
          
          <div className='p-6'>
            <div className='space-y-3'>
              <button
                onClick={() => {
                  handleEnterStore();
                  setShowNavigation(false);
                }}
                className='w-full text-left p-4 rounded-lg hover:bg-gray-50 flex items-center gap-4 border transition-colors'
                style={{ borderColor: storeTheme?.primaryColor + '20' || '#e5e7eb' }}
              >
                <div className='p-3 rounded-lg' style={{ backgroundColor: storeTheme?.primaryColor + '10' || '#eef2ff' }}>
                  <span className='text-2xl'>🛍️</span>
                </div>
                <div>
                  <div className='font-bold' style={{ color: storeTheme?.headingColor }}>Shop Products</div>
                  <div className='text-sm' style={{ color: storeTheme?.textColor + '80' || '#6b7280' }}>
                    Browse and purchase items
                  </div>
                </div>
              </button>
              
              <button
                onClick={() => {
                  setShowNavigation(false);
                  // Navigate to about section in store
                  handleEnterStore();
                }}
                className='w-full text-left p-4 rounded-lg hover:bg-gray-50 flex items-center gap-4 border transition-colors'
                style={{ borderColor: storeTheme?.primaryColor + '20' || '#e5e7eb' }}
              >
                <div className='p-3 rounded-lg' style={{ backgroundColor: storeTheme?.secondaryColor + '10' || '#ecfdf5' }}>
                  <span className='text-2xl'>📖</span>
                </div>
                <div>
                  <div className='font-bold' style={{ color: storeTheme?.headingColor }}>About Us</div>
                  <div className='text-sm' style={{ color: storeTheme?.textColor + '80' || '#6b7280' }}>
                    Learn about our business
                  </div>
                </div>
              </button>
              
              <button
                onClick={() => {
                  setShowNavigation(false);
                  // Handle contact navigation
                  if (storeInfo?.phone) {
                    window.location.href = `tel:${storeInfo.phone}`;
                  }
                }}
                className='w-full text-left p-4 rounded-lg hover:bg-gray-50 flex items-center gap-4 border transition-colors'
                style={{ borderColor: storeTheme?.primaryColor + '20' || '#e5e7eb' }}
              >
                <div className='p-3 rounded-lg' style={{ backgroundColor: storeTheme?.primaryColor + '10' || '#eef2ff' }}>
                  <span className='text-2xl'>📞</span>
                </div>
                <div>
                  <div className='font-bold' style={{ color: storeTheme?.headingColor }}>Contact Us</div>
                  <div className='text-sm' style={{ color: storeTheme?.textColor + '80' || '#6b7280' }}>
                    {storeInfo?.phone || 'Get in touch'}
                  </div>
                </div>
              </button>
            </div>
          </div>
          
          <div className='p-6 border-t bg-gray-50'>
            <button
              onClick={() => setShowNavigation(false)}
              className='w-full py-3 rounded-lg font-medium border transition-colors'
              style={{ 
                borderColor: storeTheme?.primaryColor || '#4f46e5',
                color: storeTheme?.primaryColor || '#4f46e5',
                backgroundColor: 'transparent'
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Add CSS animations to head
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes float {
        0%, 100% { transform: translateY(0px); }
        50% { transform: translateY(-20px); }
      }
      
      @keyframes gradient-x {
        0%, 100% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
      }
      
      @keyframes fade-in-up {
        from {
          opacity: 0;
          transform: translateY(30px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      
      @keyframes bounce-slow {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-10px); }
      }
      
      @keyframes pulse-slow {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.8; }
      }
      
      @keyframes spin-slow {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      
      @keyframes spin-reverse {
        from { transform: rotate(360deg); }
        to { transform: rotate(0deg); }
      }
      
      @keyframes shimmer {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(100%); }
      }
      
      @keyframes gradient-text {
        0%, 100% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
      }
      
      @keyframes wave {
        0%, 100% { transform: translateX(0) scaleY(1); }
        50% { transform: translateX(-10%) scaleY(0.9); }
      }
      
      @keyframes gridMove {
        0% { background-position: 0 0; }
        100% { background-position: 100px 100px; }
      }
      
      @keyframes twinkle {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
      }
      
      @keyframes shooting-star {
        0% {
          transform: translateX(0) translateY(0);
          opacity: 1;
        }
        100% {
          transform: translateX(200px) translateY(200px);
          opacity: 0;
        }
      }
      
      .animate-float {
        animation: float 6s ease-in-out infinite;
      }
      
      .animate-gradient-x {
        background-size: 200% 200%;
        animation: gradient-x 15s ease infinite;
      }
      
      .animate-fade-in-up {
        animation: fade-in-up 1s ease-out;
      }
      
      .animate-bounce-slow {
        animation: bounce-slow 2s infinite;
      }
      
      .animate-pulse-slow {
        animation: pulse-slow 2s infinite;
      }
      
      .animate-spin-slow {
        animation: spin-slow 20s linear infinite;
      }
      
      .animate-spin-reverse {
        animation: spin-reverse 15s linear infinite;
      }
      
      .animate-shimmer {
        animation: shimmer 2s infinite;
        background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
      }
      
      .animate-gradient-text {
        background-size: 200% auto;
        animation: gradient-text 3s ease-in-out infinite;
      }
      
      .animate-twinkle {
        animation: twinkle 2s ease-in-out infinite;
      }
      
      .animate-shooting-star {
        animation: shooting-star 2s linear infinite;
      }
      
      .animation-delay-200 {
        animation-delay: 200ms;
      }
      
      .animation-delay-400 {
        animation-delay: 400ms;
      }
      
      .animation-delay-600 {
        animation-delay: 600ms;
      }
      
      .animation-delay-1000 {
        animation-delay: 1000ms;
      }
      
      .animation-delay-1500 {
        animation-delay: 1500ms;
      }
      
      /* Masonry layout styles */
      .break-inside-avoid {
        break-inside: avoid;
      }
      
      /* Glass morphism effect */
      .backdrop-blur-sm {
        backdrop-filter: blur(8px);
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  if (loading) {
    return (
      <LogoLoading />
    );
  }

  // Check if landing page should be shown
  if (!layoutSettings?.showLanding) {
    handleEnterStore();
    return null;
  }

  return (
    <>
      <div className='relative' style={{ backgroundColor: storeTheme?.backgroundColor || '#ffffff' }}>
        {renderLayout()}
        
        {/* Admin Controls (Visible only in admin mode) */}
        {window.location.pathname.includes('/admin') && (
          <div className='fixed bottom-4 right-4 z-50 flex flex-col gap-2'>
            <button
              onClick={() => setShowLayoutSelector(true)}
              className='p-3 rounded-full shadow-lg backdrop-blur-sm bg-white/90 border border-gray-200 hover:bg-white flex items-center justify-center'
              title='Change Layout'
            >
              🎨
            </button>
            <button
              onClick={toggleLandingPage}
              className='p-3 rounded-full shadow-lg backdrop-blur-sm bg-white/90 border border-gray-200 hover:bg-white flex items-center justify-center'
              title={layoutSettings?.showLanding ? 'Disable Landing Page' : 'Enable Landing Page'}
            >
              {layoutSettings?.showLanding ? '👁️' : '👁️‍🗨️'}
            </button>
          </div>
        )}
      </div>
      
      <NavigationModal />
      
      <LayoutSelectorModal
        open={showLayoutSelector}
        onClose={() => setShowLayoutSelector(false)}
        onSelectLayout={handleSelectLayout}
        currentLayout={layoutSettings?.type}
        theme={storeTheme}
      />
    </>
  );
}